import { useState } from "react";
import { styled } from "styled-components";
import {
  Code2,
  Monitor,
  Server,
  BrainCircuit,
  Cloud,
  Wrench,
} from "lucide-react";

const skillGroups = {
  languages: {
    label: "Languages",
    icon: Code2,
    skills: ["Python", "Java", "TypeScript", "JavaScript", "SQL"],
  },

  frontend: {
    label: "Frontend",
    icon: Monitor,
    skills: ["React", "TypeScript", "JavaScript", "HTML", "CSS"],
  },

  backend: {
    label: "Backend",
    icon: Server,
    skills: [
      "Spring Boot",
      "FastAPI",
      "Node.js",
      "REST APIs",
      "GraphQL",
      "Apache Kafka ",
      "PostgreSQL",
      "MongoDB",
      "Redis",
    ],
  },

  aiml: {
    label: "AI / ML",
    icon: BrainCircuit,
    skills: [
      "PyTorch",
      "LangChain / LangGraph",
      "RAG Systems",
      "LLM Fine-tuning",
      "Agentic AI",
      "LLM Evaluation",
    ],
  },

  cloud: {
    label: "Cloud & DevOps",
    icon: Cloud,
    skills: [
      "AWS",
      "Azure",
      "Docker",
      "Kubernetes",
      "GitHub Actions",
      "Linux",
      "Git",
    ],
  },

  dailyTools: {
    label: "Daily Tools",
    icon: Wrench,
    skills: ["Kiro", "Codex", "Claude", "Ollama"],
  },
};

export default function MySkills() {
  const [activeGroup, setActiveGroup] = useState("languages");

  const currentGroup = skillGroups[activeGroup];

  return (
    <SkillsContainer id="skills">
      <h3 className="skills-head">My Superpowers</h3>

      <SkillsInner>
        <Navigation>
          {Object.entries(skillGroups).map(([key, group]) => {
            const Icon = group.icon;
            const isActive = activeGroup === key;

            return (
              <NavButton
                key={key}
                type="button"
                $active={isActive}
                onClick={() => setActiveGroup(key)}
              >
                <span className="nav-indicator" />

                <Icon className="nav-icon" size={18} strokeWidth={1.5} />

                <span className="nav-label">{group.label}</span>
              </NavButton>
            );
          })}
        </Navigation>

        <SkillsContent>
          <SkillsList key={activeGroup}>
            {currentGroup.skills.map((skill, index) => (
              <SkillItem key={skill}>
                <span className="skill-index">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <span className="skill-name">{skill}</span>
              </SkillItem>
            ))}
          </SkillsList>
        </SkillsContent>
      </SkillsInner>
    </SkillsContainer>
  );
}

const SkillsContainer = styled.section`
  width: 100%;
  margin: 80px 0 40px;
  padding: 40px 20px 80px;

  .skills-head {
    font-family: "Space Mono", monospace;
    color: #ffffff;
    font-weight: 400;
    font-size: 46px;
    text-align: center;
    margin: 0 0 90px;
    user-select: none;
  }

  @media (max-width: 768px) {
    margin: 50px 0 30px;
    padding: 30px 10px 60px;

    .skills-head {
      font-size: 28px;
      margin-bottom: 55px;
    }
  }
`;

const SkillsInner = styled.div`
  width: min(1050px, 100%);
  margin: 0 auto;

  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: 75px;

  @media (max-width: 900px) {
    gap: 45px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const Navigation = styled.nav`
  display: flex;
  flex-direction: column;
  height: fit-content;
  border-left: 1px solid #292929;

  @media (max-width: 768px) {
    flex-direction: row;
    width: 100%;

    overflow-x: auto;
    overscroll-behavior-x: contain;

    border-left: none;
    border-bottom: 1px solid #292929;

    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const NavButton = styled.button`
  position: relative;

  width: 100%;
  min-height: 58px;

  padding: 16px 14px 16px 21px;

  display: flex;
  align-items: center;
  gap: 13px;

  border: none;
  outline: none;

  background: ${({ $active }) =>
    $active ? "rgba(255, 255, 255, 0.025)" : "transparent"};

  color: ${({ $active }) => ($active ? "#ffffff" : "#707070")};

  font-family: inherit;
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? "400" : "300")};

  text-align: left;
  cursor: pointer;

  transition:
    color 0.25s ease,
    background 0.25s ease,
    padding-left 0.25s ease;

  .nav-indicator {
    position: absolute;
    left: -1px;
    top: 50%;

    width: 1px;
    height: ${({ $active }) => ($active ? "100%" : "0")};

    background: #ffffff;

    transform: translateY(-50%);

    transition: height 0.25s ease;
  }

  .nav-icon {
    flex-shrink: 0;

    opacity: ${({ $active }) => ($active ? "1" : "0.45")};

    transition:
      opacity 0.25s ease,
      transform 0.25s ease;
  }

  .nav-label {
    white-space: nowrap;
  }

  &:hover {
    color: #ffffff;

    padding-left: 25px;

    background: rgba(255, 255, 255, 0.025);

    .nav-icon {
      opacity: 1;
      transform: scale(1.05);
    }
  }

  &:focus-visible {
    color: #ffffff;
    outline: 1px solid #444444;
    outline-offset: -1px;
  }

  @media (max-width: 768px) {
    width: auto;
    min-width: max-content;
    min-height: auto;

    padding: 14px 17px;

    font-size: 14px;

    background: transparent;

    .nav-icon {
      width: 15px;
      height: 15px;
    }

    .nav-indicator {
      top: auto;
      bottom: -1px;
      left: 50%;

      width: ${({ $active }) => ($active ? "100%" : "0")};
      height: 1px;

      transform: translateX(-50%);

      transition: width 0.25s ease;
    }

    &:hover {
      padding-left: 17px;
      background: transparent;
    }
  }
`;

const SkillsContent = styled.div`
  min-width: 0;
`;

const SkillsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 55px;

  animation: skillsEnter 0.35s ease both;

  @keyframes skillsEnter {
    from {
      opacity: 0;
      transform: translateY(8px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SkillItem = styled.div`
  position: relative;

  min-height: 68px;
  padding: 0 10px 0 2px;

  display: flex;
  align-items: center;
  gap: 18px;

  border-bottom: 1px solid #242424;

  overflow: hidden;
  cursor: default;

  transition:
    padding-left 0.25s ease,
    border-color 0.25s ease,
    background 0.25s ease;

  &::before {
    content: "";

    position: absolute;
    left: 0;
    top: 50%;

    width: 1px;
    height: 0;

    background: #ffffff;

    transform: translateY(-50%);

    transition: height 0.25s ease;
  }

  .skill-index {
    width: 26px;
    flex-shrink: 0;

    color: #555555;

    font-size: 12px;
    font-weight: 300;

    transition:
      color 0.25s ease,
      transform 0.25s ease;
  }

  .skill-name {
    color: #bababa;

    font-size: 18px;
    font-weight: 300;

    transition:
      color 0.25s ease,
      transform 0.25s ease;
  }

  &:hover {
    padding-left: 10px;

    border-color: #353535;

    background: rgba(255, 255, 255, 0.018);

    &::before {
      height: 26px;
    }

    .skill-index {
      color: #888888;
      transform: translateX(2px);
    }

    .skill-name {
      color: #ffffff;
      transform: translateX(2px);
    }
  }

  @media (max-width: 768px) {
    min-height: 58px;

    .skill-index {
      width: 24px;
      font-size: 10px;
    }

    .skill-name {
      font-size: 15px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    &::before,
    .skill-index,
    .skill-name {
      transition: none;
    }
  }
`;
